import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
} from '@mui/material';
import { useEffect, useState } from 'react';
import {
  ILegalPhase,
  ILegalPhaseFullObject,
} from '../../../Interfaces/ILegalPhase';
import { ILegalPhaseTemplateFullObject } from '../../../Interfaces/ILegalPhaseTemplate';
import { ILegalRequirementTemplateDeadline } from '../../../Interfaces/ILegalRequirementTemplate_deadlines';
import { ILegalRequirementDeadline } from '../../../Interfaces/ILegalRequirement_deadlines';
import {
  ILegalTransactionType,
  ILegalTransactionTypeFullObject,
} from '../../../Interfaces/ILegalTransactionType';
import { LoadingSpinner } from '../../../core/LoadingSpinner';
import { getNewReq } from '../../../core/generic_helper';
import { getFetch } from '../../../hooks/useFetch';
import { LegalPhaseTable } from './copy/LegalPhaseTable';

interface CopyFromTransactionTypeProps {
  transactionTypes: ILegalTransactionType[];
  open: boolean;
  onClose: () => void;
  legalPhaseArray: ILegalPhaseFullObject[];
  setLegalPhaseArray: Function;
  idLegalTransaction: number;
}

export const CopyFromTransactionType: React.FC<
  CopyFromTransactionTypeProps
> = ({
  transactionTypes,
  open,
  onClose,
  legalPhaseArray,
  setLegalPhaseArray,
  idLegalTransaction,
}) => {
  const [selectedTransactionType, setSelectedTransactionType] =
    useState<ILegalTransactionType | null>(null);

  const [isLoadingFullObject, setIsLoadingFullObject] = useState(false);

  const [fullObject, setFullObject] = useState<
    ILegalTransactionTypeFullObject | undefined
  >(undefined);

  const [choosenPhases, setChoosenphases] = useState<
    ILegalPhaseTemplateFullObject[]
  >([]);

  const getNewPhase = (id: number) => {
    return {
      idLegalPhase: id,
      InfoText: null,
      isComplete: false,
      LegalPhase: '',
      LegalRequirementArray: [],
      Position: 0,
      PreconditionArray: [],
      idAdministrativeUnit: null,
      AdministrativeUnit: null,
    } as ILegalPhaseFullObject;
  };

  const handleCopy = () => {
    let phasesToKeep = [...choosenPhases];
    let newPhases: ILegalPhaseFullObject[] = [];

    // Erstellen von Zuordnungen für neue IDs
    const phaseIdMapping: { [key: number]: number } = {}; // Map von Template-ID zu neuer ID
    const requirementIdMapping: { [key: number]: number } = {}; // Map von Template-ID zu neuer ID

    phasesToKeep = phasesToKeep?.sort((a, b) => a.Position - b.Position);
    let legalPhaseId = -1;
    let legalRequirementId = -1;

    /// if: Prüfen ob legalPhase-ID angepasst werden müssen
    if (legalPhaseArray.length > 0) {
      const minIdArray =
        Math.min(...legalPhaseArray.map(x => x.idLegalPhase)) - 1;

      if (minIdArray < 0) {
        legalPhaseId = minIdArray - 1;
      }
    }
    /// end: if

    if (phasesToKeep !== undefined) {
      for (let i = 0; i < phasesToKeep.length; i++) {
        // Neue Phase erstellen
        const newPhase = getNewPhase(legalPhaseId);
        phaseIdMapping[phasesToKeep[i].idLegalPhaseTemplate] = legalPhaseId; // Mappe alte zu neuer ID
        legalPhaseId--; // ID für nächste Phase dekrementieren

        newPhase.Position = i;
        newPhase.idAdministrativeUnit = phasesToKeep[i].idAdminstrativeUnit;
        newPhase.InfoText = phasesToKeep[i].InfoText;
        newPhase.LegalPhase = phasesToKeep[i].LegalPhaseTemplate;

        newPhase.PreconditionArray = phasesToKeep[i]?.PreconditionArray.map(
          precondition => {
            return {
              idLegalPhase: precondition.idLegalPhaseTemplate,
              LegalPhase: precondition.LegalPhaseTemplate,
              isComplete: false,
              InfoText: precondition.InfoText,
              Position: precondition.Position,
              idAdministrativeUnit: precondition.idAdminstrativeUnit,
              AdministrativeUnit: null,
            };
          }
        );

        // Anforderungen der Phase kopieren
        const lrArray = phasesToKeep[i].LegalRequirementArray.sort(
          (a, b) => a.Position - b.Position
        );

        for (let z = 0; z < lrArray.length; z++) {
          // Neues Requirement erstellen
          const newReq = getNewReq(legalRequirementId);
          requirementIdMapping[lrArray[z].idLegalRequirementTemplate] =
            legalRequirementId; // Mappe alte zu neuer ID
          legalRequirementId--; // ID für nächstes Requirement dekrementieren

          newReq.Position = z;
          newReq.LegalRequirement = lrArray[z].LegalRequirementTemplate;
          newReq.canUploadFilesClient = lrArray[z].canUploadFilesClient;
          newReq.canUploadFilesEmployees = lrArray[z].canUploadFilesEmployees;
          newReq.sendMail = lrArray[z].sendMail;
          newReq.isInterneLegalRequirement =
            lrArray[z].isInterneLegalRequirement;
          newReq.EmailBody = lrArray[z].EmailBody;
          newReq.EmailSubject = lrArray[z].EmailSubject;
          newReq.TextOfError = lrArray[z].TextOfError;
          newReq.hasError = lrArray[z].hasError;
          newReq.enableFreeTextError = lrArray[z].hasErrorFreeText;
          newReq.idLegalRequirementTemplate =
            lrArray[z].idLegalRequirementTemplate;
          newReq.idLegalRequirementState = lrArray[z].idLegalRequirementState;
          newReq.InfoText = lrArray[z].InfoText;
          newReq.initialProcessingTime = lrArray[z].initialProcessingTime || 14;
          newReq.ManualProcessingTime = false;
          newReq.UserDeadlineNotified = false;
          newReq.isComplete = false;
          newReq.User_idUser = lrArray[z].idUser;
          newReq.actualProcessingTime = undefined;
          newReq.Court = lrArray[z].Court || '';
          newReq.CourtRole = lrArray[z].CourtRole || '';
          newReq.idCourt = lrArray[z].idCourt || undefined;
          newReq.idCourtRole = lrArray[z].idCourtRole || undefined;
          newReq.DocumentArrayEmployees = lrArray[z].DocumentArray || [];

          // Deadlines umwandeln und zuweisen
          if (lrArray[z].DeadlineArray !== undefined) {
            const newDeadlines = mapDeadlines(
              lrArray[z].DeadlineArray!,
              phaseIdMapping,
              requirementIdMapping
            );
            newReq.DeadlineArray = newDeadlines; // Deadlines setzen
          } else {
            newReq.DeadlineArray = [];
          }

          let idCounter = 1;

          if (newReq.DocumentArrayEmployees) {
            newReq.DocumentArrayEmployees = newReq.DocumentArrayEmployees.map(
              doc => ({
                ...doc,
                idDocument: -idCounter++, // ID zuweisen, beginnend bei -1
              })
            );
          }

          // Requirement zur Phase hinzufügen
          newPhase.LegalRequirementArray.push(newReq);
        }
        // Neue Phase zur Liste hinzufügen
        newPhases.push(newPhase);
      }
    }

    // Nachbereitung der Preconditions
    newPhases = newPhases.map(newPhase => {
      if (newPhase.PreconditionArray.length === 0) {
        return newPhase;
      } else {
        const newPrecondition: ILegalPhase[] = [];
        newPhase.PreconditionArray.map(predCon => {
          let conPhase = newPhases.find(
            con => con.idLegalPhase === phaseIdMapping[predCon.idLegalPhase]
          );
          if (conPhase) {
            newPrecondition.push(conPhase);
          }
        });
        return {
          ...newPhase,
          PreconditionArray: newPrecondition,
        };
      }
    });
    // Update des legalPhaseArray mit den neuen Phasen
    //get the max position of the existing phases
    const maxPosition = Math.max(
      ...newPhases.map(x => x.Position),
      ...legalPhaseArray.map(x => x.Position)
    );
    //shift the positions of the new phases by the max position
    newPhases = newPhases.map(x => {
      x.Position += maxPosition + 1;
      return x;
    });
    setLegalPhaseArray([...legalPhaseArray, ...newPhases]);
    wrapperOnClose();
  };

  const mapDeadlines = (
    templateDeadlines: ILegalRequirementTemplateDeadline[],
    phaseIdMapping: { [key: number]: number },
    requirementIdMapping: { [key: number]: number }
  ): ILegalRequirementDeadline[] => {
    const newDeadlines: ILegalRequirementDeadline[] = [];

    templateDeadlines.forEach(templateDeadline => {
      let newPhaseId = undefined;
      if (
        templateDeadline.idLegalPhaseTemplate !== undefined &&
        templateDeadline.idLegalPhaseTemplate !== null
      ) {
        newPhaseId = phaseIdMapping[templateDeadline.idLegalPhaseTemplate];
        if (newPhaseId === undefined) {
          return;
        }
      }
      let newRequirementId = undefined;
      if (
        templateDeadline.idLegalRequirementTemplate !== undefined &&
        templateDeadline.idLegalRequirementTemplate !== null
      ) {
        newRequirementId =
          requirementIdMapping[templateDeadline.idLegalRequirementTemplate];
        if (newRequirementId === undefined) {
          return;
        }
      }

      // Wenn Phase und/oder Requirement übernommen wurden (neue IDs existieren)

      newDeadlines.push({
        idDeadline: templateDeadline.idDeadline,
        idLegalRequirement: newRequirementId, // Verwende die neue Requirement-ID
        idLegalPhase: newPhaseId, // Verwende die neue Phase-ID
        idLegalRequirementState: templateDeadline.idLegalRequirementState,
        Weighting: templateDeadline.Weighting || 0,
        LegalRequirement: templateDeadline.LegalRequirementTemplate,
        LegalPhase: templateDeadline.LegalPhaseTemplate,
        LegalRequirementState: templateDeadline.LegalRequirementState,
        Startdate: undefined, // Standardwert oder Anpassungen
        DeadlineTriggeredAt: undefined,
        LegalTransactionTitle: undefined,
        idLegalTransactionState: templateDeadline.idLegalTransactionState,
        LegalTransactionState: templateDeadline.LegalTransactionState,
        idLegalTransaction: idLegalTransaction,
      });
    });

    return newDeadlines;
  };

  const wrapperOnClose = () => {
    setFullObject(undefined);
    setChoosenphases([]);
    setSelectedTransactionType(null);
    onClose();
  };

  useEffect(() => {
    if (selectedTransactionType) {
      getFetch(
        '/legaltransactiontype/fullObject/',
        selectedTransactionType.idLegalTransactionType,
        (data: ILegalTransactionTypeFullObject) => {
          setFullObject(data);
          setChoosenphases(data.LegalPhaseTemplateArray);
        },
        undefined,
        undefined,
        setIsLoadingFullObject
      );
    }
  }, [selectedTransactionType]);

  return (
    <Dialog open={open} fullWidth maxWidth='lg' onClose={wrapperOnClose}>
      <DialogTitle
        variant='h5'
        sx={{
          borderBottom: '1px solid #ccc', // Optional: Eine Trennlinie für visuelle Klarheit
        }}
      >
        Rechtsgeschäft kopieren
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12} mt={2}>
            <Autocomplete
              fullWidth
              options={transactionTypes}
              getOptionLabel={option => option.LegalTransactionType}
              onChange={(event, value) => {
                setSelectedTransactionType(value);
              }}
              renderInput={params => (
                <TextField {...params} label='Rechtsgeschäft' />
              )}
            />
          </Grid>

          {/* Accordion für Phasen und Maßnahmen */}
          {isLoadingFullObject && <LoadingSpinner height='50vh' />}
          {fullObject && (
            <>
              <LegalPhaseTable
                legalPhaseTemplateArray={fullObject.LegalPhaseTemplateArray}
                choosenPhases={choosenPhases}
                setChoosenPhases={setChoosenphases}
              ></LegalPhaseTable>
            </>
          )}

          {/* Copy Button */}
          <Grid item xs={12} display='flex' justifyContent='flex-end'>
            <Button
              variant='contained'
              color='primary'
              onClick={handleCopy}
              disabled={!selectedTransactionType || !choosenPhases?.length}
            >
              Kopieren
            </Button>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions
        sx={{
          borderTop: '1px solid #ccc', // Optional: Eine Trennlinie für visuelle Klarheit
        }}
      >
        <Button onClick={wrapperOnClose}>Abbrechen</Button>
      </DialogActions>
    </Dialog>
  );
};
